/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BasicLayout, OrientationMap, Orientor } from '@1x2network/layout'
import {
    applySpecification,
    ContainerSpecification,
    createSprite,
    Specification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import { SplashComponent } from '@1x2network/pixi_splash'
import { GraphicMask, PositionTuple, TradReelSet } from '@1x2network/reel_set'
import gsap from 'gsap'
import { Container, IDestroyOptions } from 'pixi.js'
import { valueIds } from '../../../constants/valueIds'
import { CustomSymbol, CustomSymbolSpecification } from '../../../CustomSymbol'
import { SymbolModel } from '../../../model/SymbolModel'
import { tweenTint } from '../../../utils/spine'
import { CustomSymbolPool } from '../../CustomSymbolPool'
import { ReelFrame } from '../../ReelFrame'
import { WinValue } from '@1x2network/is_engine_communication'

/**
 * A SplashComponent that shows a preview of a spinning reel set landing a Hold and Win trigger board
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class HoldAndWinLoop extends SplashComponent<HoldAndWinLoopSpecification> {
    private reelSet: TradReelSet<CustomSymbol, SymbolModel>
    private timeline?: GSAPTimeline
    private orientor: Orientor<ContainerSpecification>

    constructor(options: { parent?: Container; layout: BasicLayout; spec: HoldAndWinLoopSpecification }) {
        super(options)

        const pool = new CustomSymbolPool(Specification.get('symbols') as CustomSymbolSpecification[], CustomSymbol)
        this.reelSet = new TradReelSet<CustomSymbol, SymbolModel>({ spec: 'reelSet', parent: this, pool })
        new GraphicMask({ spec: 'reelSetMask', reelSet: this.reelSet.spinContainer })
        createSprite({
            spec: Specification.get('reelBackground') as SpriteSpecification,
            parent: this.reelSet,
        })
        new ReelFrame('reelFrame', this.reelSet)

        this.prepareSpin()

        this.orientor = new Orientor({
            layout: options.layout,
            property: this.specification,
            onOrient: (active) => {
                applySpecification(this, active)
            },
        })
    }

    private prepareSpin(): void {
        this.reelSet.prepareSpin([
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
            [{ id: 0 }, { id: 0 }, { id: 0 }],
        ])
    }

    public show(): void {
        super.show()

        this.animate()
    }

    public hide(): void {
        super.hide()

        this.timeline?.kill()

        this.reelSet.cancelSpin()
        this.prepareSpin()
    }

    private animate() {
        const positions: PositionTuple[] = [
            [0, 0],
            [1, 1],
            [2, 1],
            [4, 1],
            [4, 2],
        ]

        this.timeline = gsap
            .timeline({ repeat: -1, repeatDelay: 1.5 })

            // Spin
            .call(() => {
                this.reelSet.startSpin()
            })

            // Stop spinning
            .call(
                () => {
                    this.reelSet.stopSpin({ symbols: stopSpinResult })
                },
                [],
                '+=1.5',
            )

            // Animate HAW symbols, fade out non-winning symbols
            .call(
                () => {
                    const fade = this.reelSet.fetcher.getSymbolsNotInPositions(positions).map((s) => s.spine!)
                    this.reelSet.promoteSymbolsByPosition(positions)

                    tweenTint(fade, 0x7f7f7f)
                    valueIds
                        .map((id) => this.reelSet.fetcher.getSymbolsById(id))
                        .flat()
                        .forEach((symbol) => {
                            symbol.spine!.play('win')
                        })
                },
                [],
                '+=1.1',
            )

            // Reset non-winning symbols
            .call(
                () => {
                    const fade = this.reelSet.fetcher.getSymbolsNotInPositions(positions).map((s) => s.spine!)
                    tweenTint(fade, 0xffffff)
                },
                [],
                '+=1',
            )
    }

    public destroy(options?: IDestroyOptions | boolean): void {
        this.orientor.disconnect()
        super.destroy(options)
    }
}

const stopSpinResult: SymbolModel[][] = [
    [
        {
            id: 10,
            cash: {
                start: { timesStake: 1, currency: 1 } as WinValue,
                end: { timesStake: 1, currency: 1 } as WinValue,
            },
        },
        { id: 3 },
        { id: 5 },
    ],
    [
        { id: 6 },
        {
            id: 10,
            cash: {
                start: { timesStake: 2, currency: 2 } as WinValue,
                end: { timesStake: 2, currency: 2 } as WinValue,
            },
        },
        { id: 7 },
    ],
    [
        { id: 4 },
        {
            id: 9,
            cash: {
                start: { timesStake: 0, currency: 0 } as WinValue,
                end: { timesStake: 0, currency: 0 } as WinValue,
            },
        },
        {
            id: 7,
        },
    ],
    [{ id: 2 }, { id: 1 }, { id: 4 }],
    [
        { id: 1 },
        {
            id: 10,
            cash: {
                start: { timesStake: 2, currency: 2 } as WinValue,
                end: { timesStake: 2, currency: 2 } as WinValue,
            },
        },
        {
            id: 11,
            cash: {
                start: { timesStake: 20, currency: 20 } as WinValue,
                end: { timesStake: 20, currency: 20 } as WinValue,
            },
        },
    ],
]

type ContainerSpecWithOrientation = ContainerSpecification & OrientationMap<ContainerSpecification>

export type HoldAndWinLoopSpecification = ContainerSpecWithOrientation
